/**
 * Ledger Entry Editor - Mutation Queue
 *
 * Implements deterministic mutation queue with:
 * - Exact operation ordering (create entry, metadata, posted, association, parties, etc.)
 * - Deletion guard (prevents combining delete with other operations)
 * - Sequential execution (each success merges before next payload is built)
 * - Per-item loading/error tracking
 *
 * Queue order (MUST be sequential):
 * 1. Create entry (when needed)
 * 2. Entry metadata update
 * 3. Posted toggle
 * 4. Association create/update
 * 5. Payee/payer replacement (helpOrderLedgerEntryParties)
 * 6. Party replacement (same helper)
 * 7. Entry-to-entry link create/delete
 * 8. Linked-entry creation (helpOrderLinkedEntry)
 * 9. Role deletion (deleteOrderLedgerRole)
 * 10. Revise last
 */

import {
  CreateOrderLedgerEntryPayload,
  UpdateOrderLedgerEntryPayload,
  DeleteOrderLedgerEntryPayload,
  CreateOrderLedgerAssociationPayload,
  UpdateOrderLedgerAssociationPayload,
  DeleteOrderLedgerAssociationPayload,
  HelpOrderLedgerEntryPartiesPayload,
  HelpOrderLinkedEntryPayload,
  UpdateOrderLedgerPostedPayload,
  DeleteOrderLedgerRolePayload,
} from '../contracts';
import { normalizeLedgerError } from './errors';

/**
 * Mutation operation type.
 */
export type MutationOperationType =
  | 'createEntry'
  | 'updateEntry'
  | 'deleteEntry'
  | 'createAssociation'
  | 'updateAssociation'
  | 'deleteAssociation'
  | 'helpParties'
  | 'helpLinkedEntry'
  | 'updatePosted'
  | 'deleteRole'
  | 'reviseLast';

/**
 * Single queued mutation item.
 */
export type QueuedMutation = {
  type: MutationOperationType;
  payload: unknown;
  isLoading?: boolean;
  error?: Error | null;
  result?: unknown;
};

/**
 * Mutation queue with ordering and state tracking.
 */
export type MutationQueue = {
  items: QueuedMutation[];
  isExecuting: boolean;
  currentIndex: number;
  errors: Map<number, Error>;
};

/**
 * Validation result for queue building.
 */
export type QueueValidationResult = {
  valid: boolean;
  error?: string;
};

/**
 * Create an empty mutation queue.
 */
export function createEmptyQueue(): MutationQueue {
  return {
    items: [],
    isExecuting: false,
    currentIndex: 0,
    errors: new Map(),
  };
}

/**
 * Add a mutation to the queue in the correct order.
 * Enforces deterministic ordering.
 */
export function addToQueue(
  queue: MutationQueue,
  type: MutationOperationType,
  payload: unknown,
): MutationQueue {
  const newQueue = { ...queue };
  newQueue.items = [...queue.items];

  // Find insertion point based on operation order
  const operationOrder: MutationOperationType[] = [
    'createEntry',
    'updateEntry',
    'updatePosted',
    'createAssociation',
    'updateAssociation',
    'helpParties',
    'deleteAssociation',
    'helpLinkedEntry',
    'deleteRole',
    'deleteEntry',
    'reviseLast',
  ];

  const typeIndex = operationOrder.indexOf(type);
  if (typeIndex === -1) {
    throw new Error(`Unknown mutation type: ${type}`);
  }

  // Find where to insert based on order
  let insertIndex = newQueue.items.length;
  for (let i = 0; i < newQueue.items.length; i += 1) {
    const existingTypeIndex = operationOrder.indexOf(newQueue.items[i].type);
    if (existingTypeIndex > typeIndex) {
      insertIndex = i;
      break;
    }
  }

  newQueue.items.splice(insertIndex, 0, {
    type,
    payload,
    isLoading: false,
    error: null,
  });

  return newQueue;
}

/**
 * Validate queue before execution.
 * Checks for deletion guard: if deleteEntry is present, no other operations allowed.
 */
export function validateQueue(queue: MutationQueue): QueueValidationResult {
  const hasDelete = queue.items.some(item => item.type === 'deleteEntry');
  const hasOtherOps = queue.items.some(item => item.type !== 'deleteEntry');

  if (hasDelete && hasOtherOps) {
    return {
      valid: false,
      error: 'Cannot combine entry deletion with other operations',
    };
  }

  return { valid: true };
}

/**
 * Mark a queue item as loading.
 */
export function setItemLoading(
  queue: MutationQueue,
  index: number,
  isLoading: boolean,
): MutationQueue {
  const newQueue = { ...queue };
  newQueue.items = [...queue.items];
  newQueue.items[index] = {
    ...newQueue.items[index],
    isLoading,
  };
  return newQueue;
}

/**
 * Set result for a queue item.
 */
export function setItemResult(
  queue: MutationQueue,
  index: number,
  result: unknown,
): MutationQueue {
  const newQueue = { ...queue };
  newQueue.items = [...queue.items];
  newQueue.items[index] = {
    ...newQueue.items[index],
    result,
    isLoading: false,
  };
  return newQueue;
}

/**
 * Set error for a queue item.
 */
export function setItemError(
  queue: MutationQueue,
  index: number,
  error: Error,
): MutationQueue {
  const newQueue = { ...queue };
  newQueue.items = [...queue.items];
  newQueue.items[index] = {
    ...newQueue.items[index],
    error,
    isLoading: false,
  };
  newQueue.errors.set(index, error);
  return newQueue;
}

/**
 * Check if queue has any errors.
 */
export function hasQueueErrors(queue: MutationQueue): boolean {
  return queue.errors.size > 0;
}

/**
 * Get all queue errors.
 */
export function getQueueErrors(queue: MutationQueue): Map<number, Error> {
  return new Map(queue.errors);
}

/**
 * Check if all queue items succeeded.
 */
export function allQueueItemsSucceeded(queue: MutationQueue): boolean {
  return queue.items.every(item => item.result !== undefined && !item.error);
}

/**
 * Get the next item to execute.
 */
export function getNextQueueItem(queue: MutationQueue): QueuedMutation | null {
  if (queue.currentIndex >= queue.items.length) {
    return null;
  }
  return queue.items[queue.currentIndex];
}

/**
 * Advance to next queue item.
 */
export function advanceQueue(queue: MutationQueue): MutationQueue {
  return {
    ...queue,
    currentIndex: queue.currentIndex + 1,
  };
}

/**
 * Check if queue execution is complete.
 */
export function isQueueComplete(queue: MutationQueue): boolean {
  return queue.currentIndex >= queue.items.length;
}

/**
 * Get overall queue loading state.
 */
export function isQueueLoading(queue: MutationQueue): boolean {
  return queue.items.some(item => item.isLoading);
}

/**
 * Get overall queue error state.
 */
export function getQueueErrorMessage(queue: MutationQueue): string | null {
  if (queue.errors.size === 0) {
    return null;
  }

  const errors = Array.from(queue.errors.values());
  if (errors.length === 1) {
    return normalizeLedgerError(errors[0]);
  }

  return `${errors.length} operations failed`;
}

/**
 * Reset queue to initial state.
 */
export function resetQueue(): MutationQueue {
  return createEmptyQueue();
}

/**
 * Build queue from dirty fields and capabilities.
 * Returns validation result if queue is invalid.
 */
export function buildQueueFromDirtyFields(
  dirtyFields: Record<string, boolean>,
  payloads: {
    createEntry?: CreateOrderLedgerEntryPayload;
    updateEntry?: UpdateOrderLedgerEntryPayload;
    deleteEntry?: DeleteOrderLedgerEntryPayload;
    createAssociation?: CreateOrderLedgerAssociationPayload;
    updateAssociation?: UpdateOrderLedgerAssociationPayload;
    deleteAssociation?: DeleteOrderLedgerAssociationPayload;
    helpParties?: HelpOrderLedgerEntryPartiesPayload;
    helpLinkedEntry?: HelpOrderLinkedEntryPayload;
    updatePosted?: UpdateOrderLedgerPostedPayload;
    deleteRole?: DeleteOrderLedgerRolePayload;
  },
): { queue: MutationQueue; validation: QueueValidationResult } {
  let queue = createEmptyQueue();

  // Add mutations based on dirty fields
  if (dirtyFields.entryId && payloads.createEntry) {
    queue = addToQueue(queue, 'createEntry', payloads.createEntry);
  }

  const needsUpdate =
    dirtyFields.amount ||
    dirtyFields.code ||
    dirtyFields.description ||
    dirtyFields.kind ||
    dirtyFields.entryDate ||
    dirtyFields.letter ||
    dirtyFields.number ||
    dirtyFields.months ||
    dirtyFields.months_advanced ||
    dirtyFields.annuality;

  if (needsUpdate && payloads.updateEntry) {
    queue = addToQueue(queue, 'updateEntry', payloads.updateEntry);
  }

  if (dirtyFields.posted && payloads.updatePosted) {
    queue = addToQueue(queue, 'updatePosted', payloads.updatePosted);
  }

  if (dirtyFields.associationData && payloads.createAssociation) {
    queue = addToQueue(queue, 'createAssociation', payloads.createAssociation);
  }

  if (dirtyFields.associationData && payloads.updateAssociation) {
    queue = addToQueue(queue, 'updateAssociation', payloads.updateAssociation);
  }

  if (dirtyFields.parties && payloads.helpParties) {
    queue = addToQueue(queue, 'helpParties', payloads.helpParties);
  }

  if (dirtyFields.linkedEntry && payloads.helpLinkedEntry) {
    queue = addToQueue(queue, 'helpLinkedEntry', payloads.helpLinkedEntry);
  }

  if (dirtyFields.role && payloads.deleteRole) {
    queue = addToQueue(queue, 'deleteRole', payloads.deleteRole);
  }

  if (dirtyFields.deleteEntry && payloads.deleteEntry) {
    queue = addToQueue(queue, 'deleteEntry', payloads.deleteEntry);
  }

  // Validate queue
  const validation = validateQueue(queue);

  return { queue, validation };
}
